import { Table } from '@omnipresentgroup/design-system';
import { useCompany, useCompanyManagers } from '@omnipresentgroup/domains';
import {
  type ColumnDef,
  type ColumnOrderState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { useCompanyManagersTableConfig } from './hooks';
import { ColumnHeaders, type CompanyManager } from './types';

const EMPTY_MANAGERS_ARRAY: CompanyManager[] = [];

const LOADING_ARRAY = Array.from({ length: 5 }, () => ({}) as CompanyManager);

type Props = {
  companyId: string;
  optionsOverride?: Omit<TableOptions<CompanyManager>, 'data' | 'columns'>;
  columnVisibility: Partial<{
    [key in keyof typeof ColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof ColumnHeaders)[];
  columnsOverride?: ColumnDef<CompanyManager, unknown>[];
  onRowClick?: (row: CompanyManager) => void;
};

export const CompanyManagersList = ({
  companyId,
  columnVisibility,
  columnOrderOverride,
  optionsOverride,
  onRowClick,
}: Props) => {
  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(ColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columnOrderOverride || [],
  );

  const [sorting, setSorting] = useState<SortingState>([]);

  const { columns } = useCompanyManagersTableConfig({ companyId });

  const { data: company, isLoading: isLoadingCompany } = useCompany({
    id: companyId,
    enabled: !!companyId,
  });

  const { data: companyManagersData, isLoading: isLoadingCompanyManagers } =
    useCompanyManagers({
      companyId,
      enabled: !!companyId,
    });

  const isLoading = useMemo(
    () => isLoadingCompany || isLoadingCompanyManagers,
    [isLoadingCompany, isLoadingCompanyManagers],
  );

  const managers = useMemo(
    () =>
      companyManagersData?.map((item) => {
        const managerData = company?.managers?.find(
          (manager) => manager.id === item.userId,
        );
        return {
          id: item.userId,
          name: item.name,
          jobTitle: managerData?.jobTitle,
          department: managerData?.department,
          email: item.email,
          role: item.roles[0].type,
        } as CompanyManager;
      }),
    [company?.managers ?? EMPTY_MANAGERS_ARRAY, companyManagersData],
  );

  const tableOptions: Omit<
    TableOptions<CompanyManager>,
    'data' | 'columns'
  > = useMemo(
    () => ({
      state: {
        columnVisibility: columnVisibilityState,
        columnOrder,
        sorting,
      },
      getCoreRowModel: getCoreRowModel(),
      onColumnVisibilityChange: setColumnVisibilityState,
      onColumnOrderChange: setColumnOrder,
      getPaginationRowModel: getPaginationRowModel(),
      onSortingChange: setSorting,
      getSortedRowModel: getSortedRowModel(),
      ...optionsOverride,
    }),
    [columnOrder, columnVisibilityState, optionsOverride, sorting],
  );

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : managers || EMPTY_MANAGERS_ARRAY,
    columns: columns,
    ...tableOptions,
  });

  return (
    <Table
      id="company-managers-list"
      table={table}
      loading={isLoading}
      usePagination
      useSorting
      emptyStateProps={{
        id: 'company-managers-empty-list',
        title: 'There are no managers added for this company',
      }}
      onRowClick={onRowClick}
    />
  );
};
